import React, { useState, useEffect } from 'react'
import { alsokTheme } from '../styles/alsok-theme'

const MeetingRoomReservation = ({ currentUser = null, onNavigateToAdmin = null }) => {
  const [rooms, setRooms] = useState([])
  const [selectedRoom, setSelectedRoom] = useState(null)
  const [reservations, setReservations] = useState([])
  const [currentTime, setCurrentTime] = useState(new Date())
  const [showReservationForm, setShowReservationForm] = useState(false)
  const [formData, setFormData] = useState({
    meetingTitle: '',
    organizerEmail: '',
    organizerName: '',
    startTime: '',
    endTime: '',
    attendees: '',
    facePassEnabled: false,
    visitorEmail: '',
    visitorName: '',
    visitorCompany: ''
  })
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [success, setSuccess] = useState('')
  
  // 時刻更新
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date())
    }, 60000) // 1分ごとに更新
    
    return () => clearInterval(timer)
  }, [])
  
  useEffect(() => {
    fetchRooms()
    fetchTodayReservations()
  }, [])
  
  // 会議室データ取得
  const fetchRooms = async () => {
    try {
      // 実際のAPIコールを使用
      const response = await fetch('/api/meetings/rooms')
      const data = await response.json()
      
      if (data.success) {
        // APIから取得した会議室データを適切にマッピング
        const roomsWithStatus = data.rooms.map(room => ({
          ...room,
          available: room.status === 'available',
          equipment: room.equipment ? room.equipment.split(',') : [],
          features: room.features ? room.features.split(',') : [],
          currentUser: null,
          nextReservation: null,
          floorMap: {
            floor: room.location || '1F',
            position: { x: 80, y: 30 },
            route: []
          }
        }))
        
        setRooms(roomsWithStatus)
      } else {
        console.error('Failed to fetch rooms:', data.error)
        setRooms([])
      }
    } catch (error) {
      console.error('Error fetching rooms:', error)
      // エラー時のフォールバック（最小限のモックデータ）
      const mockRooms = [
        {
          id: 1,
          name: '会議室A',
          capacity: 6,
          location: '1F',
          equipment: ['プロジェクター', 'ホワイトボード', 'TV会議システム'],
          available: false,
          status: 'in_use', // 'available', 'in_use', 'maintenance'
          area: '25㎡',
          features: ['Wi-Fi完備', '空調調整可能', '自然光良好'],
          directions: '1階エレベーター前を左に進み、突き当りを右へ。ドア番号A101',
          currentUser: {
            meetingTitle: '企画会議',
            organizer: '田中太郎',
            department: '営業部',
            endTime: '15:30',
            attendees: 4
          },
          nextReservation: {
            meetingTitle: 'クライアント打合せ',
            organizer: '佐藤花子',
            department: '営業部',
            startTime: '16:00',
            endTime: '17:30',
            attendees: 3
          },
          floorMap: {
            floor: '1F',
            position: { x: 80, y: 30 },
            route: [
              { x: 50, y: 50, description: 'エントランス' },
              { x: 60, y: 50, description: 'エレベーター前' },
              { x: 60, y: 30, description: '左に進む' },
              { x: 80, y: 30, description: '会議室A' }
            ]
          }
        },
        {
          id: 2,
          name: '会議室B',
          capacity: 8,
          location: '1F',
          equipment: ['プロジェクター', 'TV会議システム', '大型ディスプレイ'],
          available: true,
          status: 'available', // 'available', 'in_use', 'maintenance'
          area: '30㎡',
          features: ['Wi-Fi完備', '空調調整可能', '遮光カーテン'],
          directions: '1階エレベーター前を左に進み、会議室Aを通り過ぎて次のドア。ドア番号B102',
          nextReservation: {
            meetingTitle: '技術会議',
            organizer: '山田健一',
            department: '技術部',
            startTime: '14:00',
            endTime: '16:00',
            attendees: 6
          },
          floorMap: {
            floor: '1F',
            position: { x: 80, y: 15 },
            route: [
              { x: 50, y: 50, description: 'エントランス' },
              { x: 60, y: 50, description: 'エレベーター前' },
              { x: 60, y: 30, description: '左に進む' },
              { x: 80, y: 30, description: '会議室Aを通過' },
              { x: 80, y: 15, description: '会議室B' }
            ]
          }
        },
        {
          id: 3,
          name: '会議室C',
          capacity: 12,
          location: '2F',
          equipment: ['プロジェクター', 'ホワイトボード', '音響設備'],
          available: false,
          status: 'in_use', // 'available', 'in_use', 'maintenance'
          area: '45㎡',
          features: ['Wi-Fi完備', '空調調整可能', '防音仕様', 'マイク設備'],
          directions: '2階エレベーターを降りて直進、最初の角を右へ。ドア番号C201',
          currentUser: {
            meetingTitle: '月次報告会',
            organizer: '鈴木みどり',
            department: '総務部',
            endTime: '17:00',
            attendees: 10
          },
          nextReservation: {
            meetingTitle: '四半期レビュー',
            organizer: '高橋雅子',
            department: '企画部',
            startTime: '18:00',
            endTime: '19:30',
            attendees: 8
          },
          floorMap: {
            floor: '2F',
            position: { x: 75, y: 35 },
            route: [
              { x: 50, y: 50, description: 'エレベーター' },
              { x: 60, y: 50, description: '直進' },
              { x: 75, y: 50, description: '右に曲がる' },
              { x: 75, y: 35, description: '会議室C' }
            ]
          }
        },
        {
          id: 4,
          name: '会議室D',
          capacity: 4,
          location: '2F',
          equipment: ['ホワイトボード', 'TV会議システム'],
          available: false, // メンテナンス中
          status: 'maintenance', // 'available', 'in_use', 'maintenance'
          area: '18㎡',
          features: ['Wi-Fi完備', '空調調整可能', 'コンパクト設計'],
          directions: '2階エレベーターを降りて直進、突き当りを左へ。ドア番号D202',
          maintenanceReason: '空調設備メンテナンス中（2025年7月末まで）',
          floorMap: {
            floor: '2F',
            position: { x: 25, y: 35 },
            route: [
              { x: 50, y: 50, description: 'エレベーター' },
              { x: 60, y: 50, description: '直進' },
              { x: 25, y: 50, description: '左に曲がる' },
              { x: 25, y: 35, description: '会議室D' }
            ]
          }
        }
      ]
      setRooms(mockRooms)
    }
  }
  
  // 今日の予約データ取得
  const fetchTodayReservations = async () => {
    try {
      // 実際のAPIコールを使用
      const response = await fetch('/api/meetings/reservations/today')
      const data = await response.json()
      
      if (data.success) {
        // APIから取得した予約データを適切にマッピング
        const formattedReservations = data.reservations.map(reservation => ({
          id: reservation.id,
          roomId: reservation.room_id,
          roomName: reservation.room_name,
          title: reservation.title,
          organizer: reservation.organizer,
          startTime: new Date(reservation.start_time).toLocaleTimeString('ja-JP', {
            hour: '2-digit',
            minute: '2-digit'
          }),
          endTime: new Date(reservation.end_time).toLocaleTimeString('ja-JP', {
            hour: '2-digit',
            minute: '2-digit'
          }),
          attendees: reservation.attendees || 0,
          facePassEnabled: reservation.face_pass_enabled || false
        }))
        
        setReservations(formattedReservations)
      } else {
        console.error('Failed to fetch reservations:', data.error)
        setReservations([])
      }
    } catch (error) {
      console.error('Error fetching reservations:', error)
      // エラー時のフォールバック（最小限のモックデータ）
      const mockReservations = [
        {
          id: 1,
          roomId: 1,
          roomName: '会議室A',
          title: '企画会議',
          organizer: '田中',
          startTime: '09:00',
          endTime: '10:30',
          attendees: 5,
          facePassEnabled: true
        },
        {
          id: 2,
          roomId: 1,
          roomName: '会議室A',
          title: 'プロジェクト進捗会議',
          organizer: '佐藤',
          startTime: '14:00',
          endTime: '15:30',
          attendees: 4,
          facePassEnabled: false
        },
        {
          id: 3,
          roomId: 3,
          roomName: '会議室C',
          title: '月次報告会',
          organizer: '山田',
          startTime: '10:00',
          endTime: '12:00',
          attendees: 10,
          facePassEnabled: false
        }
      ]
      setReservations(mockReservations)
    }
  }
  
  const handleRoomSelect = (room) => {
    setSelectedRoom(room)
    setError('')
  }
  
  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }))
  }
  
  const validateForm = () => {
    if (!formData.meetingTitle.trim()) {
      setError('会議タイトルを入力してください')
      return false
    }
    if (!formData.organizerName.trim()) {
      setError('主催者名を入力してください')
      return false
    }
    if (!formData.organizerEmail.trim()) {
      setError('主催者メールアドレスを入力してください')
      return false
    }
    if (!formData.startTime) {
      setError('開始時刻を選択してください')
      return false
    }
    if (!formData.endTime) {
      setError('終了時刻を選択してください')
      return false
    }
    
    // 顔パス有効時の追加チェック
    if (formData.facePassEnabled) {
      if (!formData.visitorName.trim()) {
        setError('顔パス機能を有効にする場合は来訪者名を入力してください')
        return false
      }
      if (!formData.visitorEmail.trim()) {
        setError('顔パス機能を有効にする場合は来訪者メールアドレスを入力してください')
        return false
      }
    }
    
    return true
  }
  
  const handleSubmit = async (e) => {
    e.preventDefault()
    if (!validateForm()) return
    
    setLoading(true)
    setError('')
    
    try {
      // 実際のAPI呼び出しを実行
      const reservationData = {
        room_id: selectedRoom.id,
        title: formData.title,
        organizer: formData.organizer,
        organizer_email: formData.organizerEmail,
        start_time: new Date(`${formData.date}T${formData.startTime}:00`).toISOString(),
        end_time: new Date(`${formData.date}T${formData.endTime}:00`).toISOString(),
        attendees: parseInt(formData.attendees),
        face_pass_enabled: formData.facePassEnabled,
        visitor_name: formData.visitorName,
        visitor_email: formData.visitorEmail
      }
      
      const response = await fetch('/api/meetings/reservations', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(reservationData)
      })
      
      const result = await response.json()
      
      if (result.success) {
        setSuccess('会議室の予約が完了しました！')
        
        // 顔パス有効時の追加メッセージ
        if (formData.facePassEnabled) {
          setTimeout(() => {
            setSuccess(prev => prev + '\n来訪者に顔認証登録用のメールを送信しました。')
          }, 1000)
        }
        
        // 予約一覧を更新
        await fetchTodayReservations()
        
      } else {
        setError(result.error || '予約の作成に失敗しました')
      }
      
      // Teams通知のシミュレーション
      setTimeout(() => {
        setSuccess(prev => prev + '\nTeamsで担当者に通知しました。')
      }, 1500)
      
      // フォームリセット
      setTimeout(() => {
        setShowReservationForm(false)
        setSelectedRoom(null)
        setFormData({
          meetingTitle: '',
          organizerEmail: '',
          organizerName: '',
          startTime: '',
          endTime: '',
          attendees: '',
          facePassEnabled: false,
          visitorEmail: '',
          visitorName: '',
          visitorCompany: ''
        })
        fetchTodayReservations() // 予約リストを更新
      }, 3000)
      
    } catch (error) {
      setError('予約の作成に失敗しました。もう一度お試しください。')
    } finally {
      setLoading(false)
    }
  }
  
  const getTimeSlots = () => {
    const slots = []
    for (let hour = 8; hour <= 20; hour++) {
      for (let minute of [0, 30]) {
        const time = `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`
        slots.push(time)
      }
    }
    return slots
  }
  
  const getRoomAvailability = (roomId) => {
    const roomReservations = reservations.filter(r => r.roomId === roomId)
    const currentHour = currentTime.getHours()
    const currentMinute = currentTime.getMinutes()
    const currentTimeStr = `${currentHour.toString().padStart(2, '0')}:${currentMinute.toString().padStart(2, '0')}`
    
    // 現在時刻に基づく簡単な空き判定
    const isCurrentlyOccupied = roomReservations.some(r => {
      return currentTimeStr >= r.startTime && currentTimeStr < r.endTime
    })
    
    return isCurrentlyOccupied ? 'occupied' : 'available'
  }
  
  return (
    <div style={{
      minHeight: '100vh',
      background: alsokTheme.gradients.security,
      padding: '20px',
      fontFamily: alsokTheme.typography.fontFamily.primary
    }}>
      <div style={{
        maxWidth: '1200px',
        margin: '0 auto',
        background: 'rgba(255, 255, 255, 0.95)',
        borderRadius: '20px',
        padding: '30px',
        boxShadow: '0 20px 60px rgba(0, 0, 0, 0.1)',
        backdropFilter: 'blur(10px)'
      }}>
        {/* ヘッダー */}
        <div style={{
          background: `linear-gradient(135deg, ${alsokTheme.primary.main} 0%, ${alsokTheme.primary.light} 100%)`,
          borderRadius: alsokTheme.borderRadius.xl,
          padding: '25px 35px',
          marginBottom: '30px',
          color: alsokTheme.neutral.white,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          boxShadow: alsokTheme.shadows.security,
          border: `1px solid ${alsokTheme.primary.light}`
        }}>
          <div>
            <h1 style={{
              margin: 0,
              fontSize: '32px',
              fontWeight: '700',
              display: 'flex',
              alignItems: 'center',
              gap: '15px'
            }}>
              <span style={{
                background: 'rgba(255, 255, 255, 0.2)',
                borderRadius: '12px',
                padding: '8px 12px',
                fontSize: '28px'
              }}>🏢</span>
              会議室案内・詳細情報
            </h1>
            <p style={{
              margin: '8px 0 0 0',
              fontSize: '16px',
              opacity: 0.9
            }}>
              会議室の場所と詳細情報をご確認いただけます
            </p>
          </div>
          <div style={{
            textAlign: 'right',
            display: 'flex',
            alignItems: 'center',
            gap: '15px'
          }}>
            {/* 管理画面遷移ボタン（管理者のみ表示） */}
            {currentUser && currentUser.role !== 'reception-only' && onNavigateToAdmin && (
              <button 
                onClick={onNavigateToAdmin}
                style={{
                  background: `rgba(255, 255, 255, 0.2)`,
                  border: `2px solid rgba(255, 255, 255, 0.3)`,
                  borderRadius: alsokTheme.borderRadius.lg,
                  padding: '10px 16px',
                  color: alsokTheme.neutral.white,
                  fontSize: '14px',
                  fontWeight: alsokTheme.typography.fontWeight.semibold,
                  cursor: 'pointer',
                  transition: alsokTheme.animation.transition,
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  boxShadow: alsokTheme.shadows.sm
                }}
                onMouseEnter={(e) => {
                  e.target.style.background = 'rgba(255, 255, 255, 0.3)'
                  e.target.style.transform = 'translateY(-2px)'
                }}
                onMouseLeave={(e) => {
                  e.target.style.background = 'rgba(255, 255, 255, 0.2)'
                  e.target.style.transform = 'translateY(0)'
                }}
                title="管理画面へ移動"
              >
                <span style={{ fontSize: '16px' }}>⚙️</span>
                管理画面
              </button>
            )}
            
            <div style={{
              background: 'rgba(255, 255, 255, 0.2)',
              borderRadius: '12px',
              padding: '12px 20px',
              fontSize: '16px',
              fontWeight: '600'
            }}>
              <div style={{ fontSize: '14px', opacity: 0.8, marginBottom: '4px' }}>現在時刻</div>
              {currentTime.toLocaleString('ja-JP', {
                year: 'numeric',
                month: '2-digit',
                day: '2-digit',
                hour: '2-digit',
                minute: '2-digit'
              })}
            </div>
          </div>
        </div>
      
        {/* エラー・成功メッセージ */}
        {error && (
          <div style={{
            background: 'linear-gradient(135deg, #ff6b6b 0%, #ee5a24 100%)',
            color: 'white',
            borderRadius: '12px',
            padding: '16px 20px',
            marginBottom: '20px',
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
            boxShadow: '0 8px 25px rgba(255, 107, 107, 0.3)',
            animation: 'slideIn 0.3s ease-out'
          }}>
            <span style={{ fontSize: '20px' }}>⚠️</span>
            <span style={{ flex: 1, fontSize: '16px' }}>{error}</span>
            <button 
              onClick={() => setError('')}
              style={{
                background: 'rgba(255, 255, 255, 0.2)',
                border: 'none',
                color: 'white',
                borderRadius: '6px',
                padding: '6px 10px',
                cursor: 'pointer',
                fontSize: '16px',
                fontWeight: 'bold'
              }}
            >
              ×
            </button>
          </div>
        )}
        
        {success && (
          <div style={{
            background: 'linear-gradient(135deg, #00b894 0%, #00a085 100%)',
            color: 'white',
            borderRadius: '12px',
            padding: '16px 20px',
            marginBottom: '20px',
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
            boxShadow: '0 8px 25px rgba(0, 184, 148, 0.3)',
            animation: 'slideIn 0.3s ease-out'
          }}>
            <span style={{ fontSize: '20px' }}>✅</span>
            <div style={{ flex: 1, fontSize: '16px', whiteSpace: 'pre-line' }}>{success}</div>
            <button 
              onClick={() => setSuccess('')}
              style={{
                background: 'rgba(255, 255, 255, 0.2)',
                border: 'none',
                color: 'white',
                borderRadius: '6px',
                padding: '6px 10px',
                cursor: 'pointer',
                fontSize: '16px',
                fontWeight: 'bold'
              }}
            >
              ×
            </button>
          </div>
        )}
      
      {!selectedRoom ? (
        <>
          {/* 会議室一覧 */}
          <div style={{ marginBottom: '40px' }}>
            <h2 style={{
              fontSize: '24px',
              fontWeight: '600',
              color: '#2d3748',
              marginBottom: '20px',
              display: 'flex',
              alignItems: 'center',
              gap: '10px'
            }}>
              <span style={{
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                borderRadius: '8px',
                padding: '6px 10px',
                fontSize: '20px'
              }}>🏢</span>
              会議室一覧
            </h2>
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(350px, 1fr))',
              gap: '20px'
            }}>
              {rooms.map(room => {
                const availability = getRoomAvailability(room.id)
                const isAvailable = availability === 'available' && room.available
                
                return (
                  <div 
                    key={room.id} 
                    onClick={() => handleRoomSelect(room)}
                    style={{
                      background: 
                        room.status === 'available' 
                          ? 'linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)'
                          : 'linear-gradient(135deg, #f1f3f4 0%, #e8eaed 100%)',
                      borderRadius: '16px',
                      padding: '24px',
                      cursor: 'pointer',
                      transition: 'all 0.3s ease',
                      border: room.status === 'available' ? 'none' : '2px solid #e0e0e0',
                      boxShadow: room.status === 'available' 
                        ? '0 8px 32px rgba(0, 0, 0, 0.08)' 
                        : '0 4px 16px rgba(0, 0, 0, 0.04)',
                      position: 'relative',
                      overflow: 'hidden',
                      transform: 'translateY(0)',
                      opacity: room.status === 'available' ? 1 : 0.6
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.transform = 'translateY(-4px)'
                      e.currentTarget.style.boxShadow = '0 12px 40px rgba(102, 126, 234, 0.15)'
                      e.currentTarget.style.border = '2px solid #667eea'
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.transform = 'translateY(0)'
                      e.currentTarget.style.boxShadow = room.status === 'available' 
                        ? '0 8px 32px rgba(0, 0, 0, 0.08)' 
                        : '0 4px 16px rgba(0, 0, 0, 0.04)'
                      e.currentTarget.style.border = room.status === 'available' ? 'none' : '2px solid #e0e0e0'
                    }}
                    onFocus={(e) => {
                      e.currentTarget.style.transform = 'translateY(-4px)'
                      e.currentTarget.style.boxShadow = '0 12px 40px rgba(102, 126, 234, 0.15)'
                      e.currentTarget.style.border = '2px solid #667eea'
                    }}
                    onBlur={(e) => {
                      e.currentTarget.style.transform = 'translateY(0)'
                      e.currentTarget.style.boxShadow = room.status === 'available' 
                        ? '0 8px 32px rgba(0, 0, 0, 0.08)' 
                        : '0 4px 16px rgba(0, 0, 0, 0.04)'
                      e.currentTarget.style.border = room.status === 'available' ? 'none' : '2px solid #e0e0e0'
                    }}
                  >
                    {/* ステータスバッジ */}
                    <div style={{
                      position: 'absolute',
                      top: '16px',
                      right: '16px',
                      background: 
                        room.status === 'available' 
                          ? 'linear-gradient(135deg, #00b894 0%, #00a085 100%)'
                          : room.status === 'in_use'
                          ? 'linear-gradient(135deg, #ff6b6b 0%, #ee5a24 100%)'
                          : 'linear-gradient(135deg, #6c757d 0%, #495057 100%)',
                      color: 'white',
                      borderRadius: '20px',
                      padding: '6px 12px',
                      fontSize: '12px',
                      fontWeight: '600',
                      boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)'
                    }}>
                      {room.status === 'available' 
                        ? '✓ 空室' 
                        : room.status === 'in_use'
                        ? '✕ 使用中'
                        : '🔧 メンテナンス中'}
                    </div>
                    
                    {/* 会議室名 */}
                    <h3 style={{
                      fontSize: '22px',
                      fontWeight: '700',
                      color: room.status === 'available' ? '#2d3748' : '#718096',
                      marginBottom: '16px',
                      marginTop: 0
                    }}>
                      {room.name}
                    </h3>
                    
                    {/* 基本情報 */}
                    <div style={{
                      display: 'flex',
                      gap: '16px',
                      marginBottom: '16px'
                    }}>
                      <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px',
                        background: 'rgba(102, 126, 234, 0.1)',
                        borderRadius: '8px',
                        padding: '8px 12px',
                        fontSize: '14px',
                        fontWeight: '600',
                        color: '#667eea'
                      }}>
                        👥 {room.capacity}名
                      </div>
                      <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px',
                        background: 'rgba(118, 75, 162, 0.1)',
                        borderRadius: '8px',
                        padding: '8px 12px',
                        fontSize: '14px',
                        fontWeight: '600',
                        color: '#764ba2'
                      }}>
                        📍 {room.location}
                      </div>
                    </div>
                    
                    {/* 設備情報 */}
                    <div style={{
                      display: 'flex',
                      flexWrap: 'wrap',
                      gap: '8px',
                      marginBottom: '16px'
                    }}>
                      {room.equipment.map((item, index) => (
                        <span 
                          key={index}
                          style={{
                            background: 'linear-gradient(135deg, #f7fafc 0%, #edf2f7 100%)',
                            color: '#4a5568',
                            borderRadius: '6px',
                            padding: '4px 8px',
                            fontSize: '12px',
                            fontWeight: '500',
                            border: '1px solid #e2e8f0'
                          }}
                        >
                          {item}
                        </span>
                      ))}
                    </div>

                    {/* 使用中の情報 */}
                    {room.currentUser && (
                      <div style={{
                        background: 'linear-gradient(135deg, #ff6b6b 0%, #ee5a24 100%)',
                        color: 'white',
                        borderRadius: '8px',
                        padding: '12px',
                        marginBottom: '12px',
                        fontSize: '13px'
                      }}>
                        <div style={{ fontWeight: '600', marginBottom: '4px' }}>
                          🔴 使用中: {room.currentUser.meetingTitle}
                        </div>
                        <div style={{ opacity: 0.9, fontSize: '12px' }}>
                          主催者: {room.currentUser.organizer} ({room.currentUser.department})
                        </div>
                        <div style={{ opacity: 0.9, fontSize: '12px' }}>
                          終了予定: {room.currentUser.endTime} | 参加者: {room.currentUser.attendees}名
                        </div>
                      </div>
                    )}

                    {/* 次の予約情報 */}
                    {room.nextReservation && (
                      <div style={{
                        background: 'linear-gradient(135deg, #74b9ff 0%, #0984e3 100%)',
                        color: 'white',
                        borderRadius: '8px',
                        padding: '12px',
                        marginBottom: '12px',
                        fontSize: '13px'
                      }}>
                        <div style={{ fontWeight: '600', marginBottom: '4px' }}>
                          📅 次の予約: {room.nextReservation.meetingTitle}
                        </div>
                        <div style={{ opacity: 0.9, fontSize: '12px' }}>
                          主催者: {room.nextReservation.organizer} ({room.nextReservation.department})
                        </div>
                        <div style={{ opacity: 0.9, fontSize: '12px' }}>
                          時間: {room.nextReservation.startTime}-{room.nextReservation.endTime} | 参加者: {room.nextReservation.attendees}名
                        </div>
                      </div>
                    )}
                    
                    {/* 詳細・案内ボタン */}
                    <div style={{
                      background: room.status === 'available' 
                        ? 'linear-gradient(135deg, #28a745 0%, #20c997 100%)'
                        : 'linear-gradient(135deg, #6c757d 0%, #495057 100%)',
                      color: 'white',
                      borderRadius: '8px',
                      padding: '10px',
                      textAlign: 'center',
                      fontSize: '14px',
                      fontWeight: '600',
                      marginTop: '8px',
                      cursor: 'pointer'
                    }} onClick={() => setSelectedRoom(room)}>
                      📍 詳細・案内
                    </div>
                    
                  </div>
                )
              })}
            </div>
          </div>
          
          {/* 本日の予約状況 */}
          <div style={{ marginBottom: '40px' }}>
            <h2 style={{
              fontSize: '24px',
              fontWeight: '600',
              color: '#2d3748',
              marginBottom: '20px',
              display: 'flex',
              alignItems: 'center',
              gap: '10px'
            }}>
              <span style={{
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                borderRadius: '8px',
                padding: '6px 10px',
                fontSize: '20px'
              }}>📋</span>
              本日の予約状況
            </h2>
            <div style={{
              background: 'linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)',
              borderRadius: '16px',
              padding: '24px',
              boxShadow: '0 8px 32px rgba(0, 0, 0, 0.08)',
              border: '1px solid #e2e8f0'
            }}>
              {reservations.length > 0 ? (
                <div style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '16px'
                }}>
                  {reservations.map(reservation => (
                    <div 
                      key={reservation.id}
                      style={{
                        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                        borderRadius: '12px',
                        padding: '20px',
                        color: 'white',
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        boxShadow: '0 6px 20px rgba(102, 126, 234, 0.2)'
                      }}
                    >
                      <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '20px'
                      }}>
                        <div style={{
                          background: 'rgba(255, 255, 255, 0.2)',
                          borderRadius: '8px',
                          padding: '12px 16px',
                          fontWeight: '700',
                          fontSize: '16px',
                          minWidth: '120px',
                          textAlign: 'center'
                        }}>
                          {reservation.startTime} - {reservation.endTime}
                        </div>
                        <div>
                          <div style={{
                            fontSize: '18px',
                            fontWeight: '700',
                            marginBottom: '8px'
                          }}>
                            {reservation.title}
                          </div>
                          <div style={{
                            display: 'flex',
                            gap: '16px',
                            alignItems: 'center',
                            fontSize: '14px',
                            opacity: 0.9
                          }}>
                            <span style={{
                              background: 'rgba(255, 255, 255, 0.2)',
                              borderRadius: '6px',
                              padding: '4px 8px',
                              fontWeight: '600'
                            }}>
                              🏢 {reservation.roomName}
                            </span>
                            <span>👤 主催: {reservation.organizer}</span>
                            <span>👥 {reservation.attendees}名</span>
                          </div>
                        </div>
                      </div>
                      <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px'
                      }}>
                        {reservation.facePassEnabled && (
                          <div style={{
                            background: 'rgba(255, 255, 255, 0.2)',
                            borderRadius: '20px',
                            padding: '6px 12px',
                            fontSize: '12px',
                            fontWeight: '600',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '4px'
                          }}>
                            <span style={{ fontSize: '14px' }}>🔐</span>
                            顔パス有効
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div style={{
                  textAlign: 'center',
                  padding: '40px 20px',
                  color: '#718096',
                  fontSize: '16px'
                }}>
                  <div style={{
                    fontSize: '48px',
                    marginBottom: '16px'
                  }}>📋</div>
                  <div style={{
                    fontSize: '18px',
                    fontWeight: '600',
                    marginBottom: '8px'
                  }}>
                    本日の予約はありません
                  </div>
                  <div style={{ fontSize: '14px' }}>
                    会議室を予約して効率的なミーティングを始めましょう
                  </div>
                </div>
              )}
            </div>
          </div>
        </>
      ) : (
        /* 会議室詳細・案内画面 */
        <div style={{
          background: 'linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)',
          borderRadius: '16px',
          padding: '30px',
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.08)',
          border: '1px solid #e2e8f0'
        }}>
          {/* ヘッダー */}
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '30px',
            paddingBottom: '20px',
            borderBottom: '2px solid #e2e8f0'
          }}>
            <div>
              <h2 style={{
                fontSize: '28px',
                fontWeight: '700',
                color: '#2d3748',
                margin: '0 0 8px 0',
                display: 'flex',
                alignItems: 'center',
                gap: '12px'
              }}>
                <span style={{
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  borderRadius: '12px',
                  padding: '8px 12px',
                  fontSize: '24px'
                }}>🏢</span>
                {selectedRoom.name} - 詳細案内
              </h2>
              <p style={{
                margin: 0,
                color: '#718096',
                fontSize: '16px'
              }}>
                {selectedRoom.available ? '利用可能' : `利用不可 - ${selectedRoom.maintenanceReason || 'メンテナンス中'}`}
              </p>
            </div>
            <button 
              onClick={() => setSelectedRoom(null)}
              style={{
                background: 'linear-gradient(135deg, #6c757d 0%, #495057 100%)',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                padding: '10px 20px',
                fontSize: '14px',
                fontWeight: '600',
                cursor: 'pointer',
                transition: 'all 0.3s ease',
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}
              onMouseEnter={(e) => {
                e.target.style.transform = 'translateY(-2px)'
                e.target.style.boxShadow = '0 6px 20px rgba(108, 117, 125, 0.3)'
              }}
              onMouseLeave={(e) => {
                e.target.style.transform = 'translateY(0)'
                e.target.style.boxShadow = 'none'
              }}
            >
              ← 一覧に戻る
            </button>
          </div>

          <div style={{
            display: 'grid',
            gridTemplateColumns: '1fr 1fr',
            gap: '30px',
            alignItems: 'start'
          }}>
            {/* 左側：基本情報と案内 */}
            <div>
              {/* 基本情報 */}
              <div style={{
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                borderRadius: '12px',
                padding: '20px',
                color: 'white',
                marginBottom: '20px'
              }}>
                <h3 style={{
                  margin: '0 0 16px 0',
                  fontSize: '20px',
                  fontWeight: '600'
                }}>📊 基本情報</h3>
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: '1fr 1fr',
                  gap: '12px'
                }}>
                  <div style={{
                    background: 'rgba(255, 255, 255, 0.2)',
                    borderRadius: '8px',
                    padding: '12px',
                    textAlign: 'center'
                  }}>
                    <div style={{ fontSize: '24px', marginBottom: '4px' }}>👥</div>
                    <div style={{ fontSize: '14px', fontWeight: '600' }}>{selectedRoom.capacity}名</div>
                  </div>
                  <div style={{
                    background: 'rgba(255, 255, 255, 0.2)',
                    borderRadius: '8px',
                    padding: '12px',
                    textAlign: 'center'
                  }}>
                    <div style={{ fontSize: '24px', marginBottom: '4px' }}>📍</div>
                    <div style={{ fontSize: '14px', fontWeight: '600' }}>{selectedRoom.location}</div>
                  </div>
                  <div style={{
                    background: 'rgba(255, 255, 255, 0.2)',
                    borderRadius: '8px',
                    padding: '12px',
                    textAlign: 'center'
                  }}>
                    <div style={{ fontSize: '24px', marginBottom: '4px' }}>📐</div>
                    <div style={{ fontSize: '14px', fontWeight: '600' }}>{selectedRoom.area}</div>
                  </div>
                  <div style={{
                    background: 'rgba(255, 255, 255, 0.2)',
                    borderRadius: '8px',
                    padding: '12px',
                    textAlign: 'center'
                  }}>
                    <div style={{ fontSize: '24px', marginBottom: '4px' }}>
                      {selectedRoom.available ? '✅' : '❌'}
                    </div>
                    <div style={{ fontSize: '14px', fontWeight: '600' }}>
                      {selectedRoom.available ? '利用可能' : '利用不可'}
                    </div>
                  </div>
                </div>
              </div>

              {/* 設備情報 */}
              <div style={{
                background: 'linear-gradient(135deg, #28a745 0%, #20c997 100%)',
                borderRadius: '12px',
                padding: '20px',
                color: 'white',
                marginBottom: '20px'
              }}>
                <h3 style={{
                  margin: '0 0 16px 0',
                  fontSize: '20px',
                  fontWeight: '600'
                }}>🔧 設備・機器</h3>
                <div style={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: '8px'
                }}>
                  {selectedRoom.equipment.map((item, index) => (
                    <span 
                      key={index}
                      style={{
                        background: 'rgba(255, 255, 255, 0.2)',
                        borderRadius: '6px',
                        padding: '6px 12px',
                        fontSize: '14px',
                        fontWeight: '500'
                      }}
                    >
                      {item}
                    </span>
                  ))}
                </div>
              </div>

              {/* 特徴 */}
              <div style={{
                background: 'linear-gradient(135deg, #ffc107 0%, #fd7e14 100%)',
                borderRadius: '12px',
                padding: '20px',
                color: 'white',
                marginBottom: '20px'
              }}>
                <h3 style={{
                  margin: '0 0 16px 0',
                  fontSize: '20px',
                  fontWeight: '600'
                }}>⭐ 特徴</h3>
                <div style={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: '8px'
                }}>
                  {selectedRoom.features.map((feature, index) => (
                    <span 
                      key={index}
                      style={{
                        background: 'rgba(255, 255, 255, 0.2)',
                        borderRadius: '6px',
                        padding: '6px 12px',
                        fontSize: '14px',
                        fontWeight: '500'
                      }}
                    >
                      {feature}
                    </span>
                  ))}
                </div>
              </div>

              {/* アクセス案内 */}
              <div style={{
                background: 'linear-gradient(135deg, #17a2b8 0%, #138496 100%)',
                borderRadius: '12px',
                padding: '20px',
                color: 'white'
              }}>
                <h3 style={{
                  margin: '0 0 16px 0',
                  fontSize: '20px',
                  fontWeight: '600'
                }}>🚶‍♂️ アクセス案内</h3>
                <p style={{
                  margin: 0,
                  fontSize: '16px',
                  lineHeight: '1.6',
                  background: 'rgba(255, 255, 255, 0.1)',
                  borderRadius: '8px',
                  padding: '12px'
                }}>
                  {selectedRoom.directions}
                </p>
              </div>
            </div>

            {/* 右側：フロアマップ */}
            <div>
              <div style={{
                background: 'linear-gradient(135deg, #343a40 0%, #495057 100%)',
                borderRadius: '12px',
                padding: '20px',
                color: 'white',
                height: 'fit-content'
              }}>
                <h3 style={{
                  margin: '0 0 20px 0',
                  fontSize: '20px',
                  fontWeight: '600',
                  textAlign: 'center'
                }}>🗺️ フロアマップ ({selectedRoom.floorMap.floor})</h3>
                
                {/* フロアマップSVG */}
                <div style={{
                  background: 'white',
                  borderRadius: '8px',
                  padding: '20px',
                  marginBottom: '20px'
                }}>
                  <svg 
                    width="100%" 
                    height="300" 
                    viewBox="0 0 100 100"
                    style={{
                      border: '2px solid #e2e8f0',
                      borderRadius: '4px'
                    }}
                  >
                    {/* フロア背景 */}
                    <rect width="100" height="100" fill="#f8f9fa" stroke="#e2e8f0" strokeWidth="1"/>
                    
                    {/* グリッド線 */}
                    {[...Array(11)].map((_, i) => (
                      <g key={i}>
                        <line x1={i * 10} y1="0" x2={i * 10} y2="100" stroke="#e9ecef" strokeWidth="0.5"/>
                        <line x1="0" y1={i * 10} x2="100" y2={i * 10} stroke="#e9ecef" strokeWidth="0.5"/>
                      </g>
                    ))}
                    
                    {/* ルート描画 */}
                    {selectedRoom.floorMap.route.map((point, index) => {
                      const nextPoint = selectedRoom.floorMap.route[index + 1]
                      return (
                        <g key={index}>
                          {/* ルートライン */}
                          {nextPoint && (
                            <line 
                              x1={point.x} 
                              y1={point.y} 
                              x2={nextPoint.x} 
                              y2={nextPoint.y} 
                              stroke="#007bff" 
                              strokeWidth="2"
                              strokeDasharray="5,5"
                            />
                          )}
                          {/* ルートポイント */}
                          <circle 
                            cx={point.x} 
                            cy={point.y} 
                            r="3" 
                            fill={index === 0 ? "#28a745" : 
                                  index === selectedRoom.floorMap.route.length - 1 ? "#dc3545" : "#007bff"}
                            stroke="white"
                            strokeWidth="2"
                          />
                          {/* ポイント番号 */}
                          <text 
                            x={point.x} 
                            y={point.y - 6} 
                            textAnchor="middle" 
                            fontSize="8" 
                            fill="#495057"
                            fontWeight="bold"
                          >
                            {index + 1}
                          </text>
                        </g>
                      )
                    })}
                    
                    {/* 目的地マーカー */}
                    <circle 
                      cx={selectedRoom.floorMap.position.x} 
                      cy={selectedRoom.floorMap.position.y} 
                      r="8" 
                      fill="#dc3545"
                      stroke="white"
                      strokeWidth="3"
                      style={{
                        animation: 'pulse 2s infinite'
                      }}
                    />
                    <text 
                      x={selectedRoom.floorMap.position.x} 
                      y={selectedRoom.floorMap.position.y + 3} 
                      textAnchor="middle" 
                      fontSize="10" 
                      fill="white"
                      fontWeight="bold"
                    >
                      🏢
                    </text>
                  </svg>
                </div>

                {/* ルート説明 */}
                <div style={{
                  background: 'rgba(255, 255, 255, 0.1)',
                  borderRadius: '8px',
                  padding: '16px'
                }}>
                  <h4 style={{
                    margin: '0 0 12px 0',
                    fontSize: '16px',
                    fontWeight: '600'
                  }}>📍 ルート案内</h4>
                  <div style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '8px'
                  }}>
                    {selectedRoom.floorMap.route.map((point, index) => (
                      <div 
                        key={index}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '8px',
                          fontSize: '14px'
                        }}
                      >
                        <span style={{
                          background: index === 0 ? '#28a745' : 
                                     index === selectedRoom.floorMap.route.length - 1 ? '#dc3545' : '#007bff',
                          color: 'white',
                          borderRadius: '50%',
                          width: '20px',
                          height: '20px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontSize: '12px',
                          fontWeight: 'bold'
                        }}>
                          {index + 1}
                        </span>
                        <span>{point.description}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* CSSアニメーション */}
      <style>{`
        @keyframes slideIn {
          from {
            opacity: 0;
            transform: translateY(-10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        
        @keyframes fadeIn {
          from {
            opacity: 0;
          }
          to {
            opacity: 1;
          }
        }
        
        @keyframes pulse {
          0% {
            transform: scale(1);
            opacity: 1;
          }
          50% {
            transform: scale(1.2);
            opacity: 0.7;
          }
          100% {
            transform: scale(1);
            opacity: 1;
          }
        }
      `}</style>
      </div>
    </div>
  )
}

export default MeetingRoomReservation